import { useAuth } from "@elit-hair/elit-tools";
import { Button, Icon } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbEdit } from "react-icons/tb";
import { toast } from "sonner";
import z from "zod";

import { AdminSurface } from "@/components/ui/admin-surface";
import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { TIME_FORMAT } from "@/constants/date-time";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import {
    DEFAULT_SCHEDULE_TIMEZONE_MODE,
    type ScheduleTimezoneMode,
} from "@/features/schedule/types/schedule-timezone.types";
import {
    getBrowserTimezone,
    projectScheduleFromStorage,
    projectScheduleToAgentTimezone,
    projectScheduleToDisplayTimezone,
    projectScheduleToStorage,
    resolveDisplayTimezone,
} from "@/features/schedule/utils/schedule-timezone.utils";
import { useCreateSchedule, useScheduleByUserId, useUpdateSchedule } from "@/hooks/queries/schedule.queries";
import { getLocalSetting } from "@/providers/general/settings-provider";
import type { ExceptionDates, WeeklyWorkingHour } from "@/types/schedule.types";
import { APP_ROUTES_TO } from "@/utils/routes";

import { SaveScheduleConfirmDialog } from "../components/save-schedule-confirm-dialog";
import {
    EditScheduleFlow,
    ReadOnlyScheduleView,
    type ScheduleViewMode,
    TimezoneModeToggle,
    TimezoneSelect,
    ViewModeToggle,
} from "../components/schedule-edit-views";

const DEFAULT_TIMEZONE = getBrowserTimezone();

const ManageScheduleSearchSchema = z.object({
    userId: z.string().optional(),
});

type ScheduleEditBaseline = {
    weeklyWorkingHours: WeeklyWorkingHour[];
    exceptionDates: ExceptionDates[];
    timezone: string;
};

export const Route = createFileRoute("/management/schedules/manage/")({
    component: ManageSchedulePage,
    validateSearch: zodValidator(ManageScheduleSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "schedule_overview_manage",
                to: APP_ROUTES_TO.MANAGEMENT_SCHEDULES_MANAGE,
            },
            title: "schedule_overview_manage",
        },
    },
});

function ManageSchedulePage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const { userId: selectedUserId } = Route.useSearch({ structuralSharing: true });
    const { userData } = useAuth();
    const [viewMode, setViewMode] = useState<ScheduleViewMode>("grid");
    const [timezoneMode, setTimezoneMode] = useState<ScheduleTimezoneMode>(DEFAULT_SCHEDULE_TIMEZONE_MODE);
    const [isEditing, setIsEditing] = useState(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

    const [localTimezone, setLocalTimezone] = useState(DEFAULT_TIMEZONE);
    const [localWeeklyHours, setLocalWeeklyHours] = useState<WeeklyWorkingHour[]>([]);
    const [localExceptionDates, setLocalExceptionDates] = useState<ExceptionDates[]>([]);
    const [editBaseline, setEditBaseline] = useState<ScheduleEditBaseline | null>(null);

    const {
        data: schedule,
        isLoading: scheduleLoading,
        isError: scheduleError,
        error: scheduleErr,
    } = useScheduleByUserId(selectedUserId);

    const createSchedule = useCreateSchedule();
    const updateSchedule = useUpdateSchedule(selectedUserId ?? "");

    const responseStatus =
        scheduleErr && typeof scheduleErr === "object" && "response" in scheduleErr
            ? (scheduleErr as { response?: { status?: number } }).response?.status
            : undefined;

    const isNotFound = scheduleError && responseStatus === 404;
    const isForbidden = scheduleError && responseStatus === 403;
    const displayFormat = getLocalSetting<string>(SETTINGS_KEYS.TIME_FORMAT) ?? TIME_FORMAT.TWENTY_FOUR_HOURS;

    const agentTimezone = localTimezone || DEFAULT_TIMEZONE;
    const displayTimezone = resolveDisplayTimezone(timezoneMode, agentTimezone);
    const canEditAgentTimezone = isEditing && timezoneMode === "agent";
    const isMutating = createSchedule.isPending || updateSchedule.isPending;
    const showScheduleContent =
        Boolean(selectedUserId) && !scheduleLoading && !isForbidden && (!scheduleError || isNotFound);

    const displayProjection = useMemo(
        () =>
            projectScheduleToDisplayTimezone(
                {
                    weeklyWorkingHours: localWeeklyHours,
                    exceptionDates: localExceptionDates,
                },
                agentTimezone,
                displayTimezone
            ),
        [localWeeklyHours, localExceptionDates, agentTimezone, displayTimezone]
    );

    const handleDisplayWeeklyHoursChange = useCallback(
        (hours: WeeklyWorkingHour[]) => {
            const projected = projectScheduleToAgentTimezone(
                hours,
                displayProjection.exceptionDates,
                displayTimezone,
                agentTimezone
            );
            setLocalWeeklyHours(projected.weeklyWorkingHours);
        },
        [displayProjection.exceptionDates, displayTimezone, agentTimezone]
    );

    const handleDisplayExceptionDatesChange = useCallback(
        (items: ExceptionDates[]) => {
            const projected = projectScheduleToAgentTimezone(
                displayProjection.weeklyWorkingHours,
                items,
                displayTimezone,
                agentTimezone
            );
            setLocalExceptionDates(projected.exceptionDates);
        },
        [displayProjection.weeklyWorkingHours, displayTimezone, agentTimezone]
    );

    useEffect(() => {
        if (isEditing) return;

        if (schedule) {
            const agentTimes = projectScheduleFromStorage(schedule, schedule.timezone);
            setLocalTimezone(schedule.timezone);
            setLocalWeeklyHours(agentTimes.weeklyWorkingHours);
            setLocalExceptionDates(agentTimes.exceptionDates);
            setEditBaseline(null);
        } else if (!scheduleLoading && selectedUserId) {
            setLocalTimezone(DEFAULT_TIMEZONE);
            setLocalWeeklyHours([]);
            setLocalExceptionDates([]);
            setEditBaseline(null);
        }
    }, [schedule, scheduleLoading, selectedUserId, isEditing]);

    const handleSave = useCallback(async () => {
        if (!schedule) return;
        try {
            const storagePayload = projectScheduleToStorage(localWeeklyHours, localExceptionDates, localTimezone);
            await updateSchedule.mutateAsync({
                id: schedule.id,
                data: {
                    timezone: localTimezone,
                    weeklyWorkingHours: storagePayload.weeklyWorkingHours,
                    exceptionDates: storagePayload.exceptionDates,
                },
            });
            toast.success(t("schedule_saved"));
            setSaveConfirmOpen(false);
            setIsEditing(false);
            setEditBaseline(null);
        } catch {
            toast.error(t("schedule_save_failed"));
        }
    }, [schedule, localTimezone, localWeeklyHours, localExceptionDates, updateSchedule, t]);

    const handleCreate = useCallback(async () => {
        if (!selectedUserId) return;
        try {
            const storagePayload = projectScheduleToStorage(localWeeklyHours, localExceptionDates, localTimezone);
            await createSchedule.mutateAsync({
                userId: selectedUserId,
                timezone: localTimezone,
                weeklyWorkingHours: storagePayload.weeklyWorkingHours,
                exceptionDates: storagePayload.exceptionDates,
            });
            toast.success(t("schedule_created"));
            setIsEditing(false);
        } catch {
            toast.error(t("schedule_create_failed"));
        }
    }, [selectedUserId, localTimezone, localWeeklyHours, localExceptionDates, createSchedule, t]);

    const handleCancelEdit = useCallback(() => {
        if (schedule) {
            const agentTimes = projectScheduleFromStorage(schedule, schedule.timezone);
            setLocalTimezone(schedule.timezone);
            setLocalWeeklyHours(agentTimes.weeklyWorkingHours);
            setLocalExceptionDates(agentTimes.exceptionDates);
        }
        setIsEditing(false);
        setEditBaseline(null);
    }, [schedule]);

    const startEditing = useCallback(
        (baseline?: ScheduleEditBaseline) => {
            if (baseline) {
                setEditBaseline(baseline);
            } else {
                // Snapshot agent-local edit state (already converted from UTC on load).
                setEditBaseline({
                    weeklyWorkingHours: localWeeklyHours,
                    exceptionDates: localExceptionDates,
                    timezone: localTimezone,
                });
            }
            setIsEditing(true);
        },
        [localWeeklyHours, localExceptionDates, localTimezone]
    );

    const handleMemberChange = useCallback(
        (memberId: string | null) => {
            setIsEditing(false);
            navigate({
                to: APP_ROUTES_TO.MANAGEMENT_SCHEDULES_MANAGE,
                search: { userId: memberId ?? undefined },
            });
        },
        [navigate]
    );

    const renderPrimaryActions = () => {
        if (!showScheduleContent) return null;

        if (isEditing) {
            return (
                <>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={schedule ? handleCancelEdit : () => setIsEditing(false)}
                        disabled={isMutating}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        onClick={schedule ? () => setSaveConfirmOpen(true) : handleCreate}
                        disabled={isMutating}
                    >
                        {schedule ? t("save") : t("create_schedule")}
                    </Button>
                </>
            );
        }

        if (isNotFound) {
            return (
                <Button type="button" size="sm" onClick={() => startEditing()}>
                    <Icon icon={TbEdit} />
                    {t("create_schedule")}
                </Button>
            );
        }

        if (schedule) {
            return (
                <Button type="button" variant="outline" size="sm" onClick={() => startEditing()}>
                    <Icon icon={TbEdit} />
                    {t("edit")}
                </Button>
            );
        }

        return null;
    };

    return (
        <div className="flex h-dvh min-h-0 flex-col gap-4 overflow-hidden">
            <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 w-full sm:w-auto sm:min-w-56 sm:max-w-xs">
                    <TeamMemberSelector
                        selectedMemberId={selectedUserId}
                        onMemberChange={handleMemberChange}
                        currentUserId={userData?.id}
                        labels={{
                            resetSelection: "my_schedule",
                        }}
                    />
                </div>

                <div className="ml-auto flex flex-wrap items-center gap-2">
                    {canEditAgentTimezone ? (
                        <TimezoneSelect
                            value={agentTimezone}
                            onChange={setLocalTimezone}
                            disabled={isMutating}
                            className="w-full max-w-56 sm:w-56"
                        />
                    ) : (
                        <div className="flex h-9 max-w-56 items-center truncate rounded-md border border-border/60 bg-muted/20 px-3 text-sm font-medium">
                            {displayTimezone}
                        </div>
                    )}

                    <TimezoneModeToggle mode={timezoneMode} onChange={setTimezoneMode} />
                    <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
                    {renderPrimaryActions()}
                </div>
            </div>

            {selectedUserId ? (
                <>
                    {scheduleLoading && (
                        <div className="min-h-0 flex-1 animate-pulse rounded-2xl border border-border/60 bg-muted/40" />
                    )}

                    {!scheduleLoading && isNotFound && !isEditing && (
                        <ReadOnlyScheduleView
                            schedule={{
                                timezone: agentTimezone,
                                weeklyWorkingHours: displayProjection.weeklyWorkingHours,
                                exceptionDates: displayProjection.exceptionDates,
                            }}
                            viewMode={viewMode}
                            timezoneMode={timezoneMode}
                        />
                    )}

                    {!scheduleLoading && schedule && !isEditing && (
                        <ReadOnlyScheduleView
                            schedule={{
                                timezone: agentTimezone,
                                weeklyWorkingHours: displayProjection.weeklyWorkingHours,
                                exceptionDates: displayProjection.exceptionDates,
                            }}
                            viewMode={viewMode}
                            timezoneMode={timezoneMode}
                        />
                    )}

                    {!scheduleLoading && isNotFound && isEditing && (
                        <EditScheduleFlow
                            weeklyHours={displayProjection.weeklyWorkingHours}
                            onWeeklyHoursChange={handleDisplayWeeklyHoursChange}
                            exceptionDates={displayProjection.exceptionDates}
                            onExceptionDatesChange={handleDisplayExceptionDatesChange}
                            viewMode={viewMode}
                            isLoading={createSchedule.isPending}
                            timezoneMode={timezoneMode}
                        />
                    )}

                    {!scheduleLoading && schedule && isEditing && (
                        <EditScheduleFlow
                            weeklyHours={displayProjection.weeklyWorkingHours}
                            onWeeklyHoursChange={handleDisplayWeeklyHoursChange}
                            exceptionDates={displayProjection.exceptionDates}
                            onExceptionDatesChange={handleDisplayExceptionDatesChange}
                            viewMode={viewMode}
                            isLoading={updateSchedule.isPending}
                            timezoneMode={timezoneMode}
                        />
                    )}

                    {saveConfirmOpen && schedule && (
                        <SaveScheduleConfirmDialog
                            open={saveConfirmOpen}
                            onOpenChange={setSaveConfirmOpen}
                            onConfirm={handleSave}
                            isSaving={updateSchedule.isPending}
                            oldWeekly={editBaseline?.weeklyWorkingHours ?? localWeeklyHours}
                            newWeekly={localWeeklyHours}
                            oldExceptions={editBaseline?.exceptionDates ?? localExceptionDates}
                            newExceptions={localExceptionDates}
                            oldTimezone={editBaseline?.timezone ?? localTimezone}
                            newTimezone={localTimezone}
                            displayFormat={displayFormat}
                        />
                    )}

                    {!scheduleLoading && isForbidden && (
                        <AdminSurface className="p-6">
                            <p className="text-sm text-muted-foreground">{t("schedule_no_permission")}</p>
                        </AdminSurface>
                    )}

                    {!scheduleLoading && scheduleError && !isNotFound && !isForbidden && (
                        <AdminSurface className="p-6">
                            <p className="text-sm text-destructive">{t("error_loading_schedule")}</p>
                        </AdminSurface>
                    )}
                </>
            ) : (
                <AdminSurface className="flex min-h-0 flex-1 items-center justify-center p-8">
                    <p className="text-center text-sm text-muted-foreground">{t("select_user_to_manage_schedule")}</p>
                </AdminSurface>
            )}
        </div>
    );
}
